import { c as _c } from "react/compiler-runtime";
import { Badge, Button, Select, Skeleton } from '@systemeio/components';
import { EmptyStateChartIcon, PlusIcon } from '@systemeio/icons';
import { CurrencyEnum } from '@systemeio/types';
import { cents, centsToUnits } from '@systemeio/util';
import { useAffiliateChartData } from 'modules/affiliate/hooks/use-affiliate-chart-data';
import { AffiliateChartInterface, AffiliateSumType } from 'modules/affiliate/types/affiliate-statistic-interface';
import { getAffiliateChartTextColor } from 'modules/affiliate/utils/get-affiliate-chart-color';
import { getAffiliateChartOptions } from 'modules/affiliate/utils/get-affiliate-chart-options';
import { getChartValue } from 'modules/affiliate/utils/get-chart-value';
import { getMainCurrency } from 'modules/affiliate/utils/get-main-currency';
import { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { ChartCard } from 'shared/components/chart/chart-card';
import { barChartPlugins } from 'shared/components/chart/chart-config';
import { EmptyState, EmptyStateProps } from 'shared/components/empty-states';
import { CurrencyNameEnum } from 'shared/enums/currency-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { currencyFormat } from 'shared/utils/currency-format';
import { toFullDate } from 'shared/utils/date-time-format';
interface AffiliateCommissionChartProps {
  height: number;
  chartData?: AffiliateChartInterface;
  /* Decides which currencies can be shown, and the main one: the currency with the most earned. */
  total?: AffiliateSumType;
  selectedCurrency?: CurrencyEnum;
  /* Shown instead of the chart: the page decides when there is no activity and what it says. */
  emptyStateProps?: EmptyStateProps;
}
export function AffiliateCommissionChart(t0) {
  const $ = _c(57);
  const {
    height,
    chartData,
    total,
    selectedCurrency,
    emptyStateProps
  } = t0;
  const {
    user,
    intlLocale
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [shownCurrencies, setShownCurrencies] = useState(t1);
  let t2;
  if ($[1] !== total) {
    t2 = total ? Object.keys(total) as CurrencyEnum[] : [];
    $[1] = total;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const allCurrencies = t2;
  let t3;
  if ($[3] !== total) {
    t3 = total ? getMainCurrency(total) : undefined;
    $[3] = total;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const mainCurrency = t3;
  const isComparable = !selectedCurrency && allCurrencies.length > 1;
  let t4;
  bb0: {
    if (selectedCurrency) {
      let t5;
      if ($[5] !== selectedCurrency) {
        t5 = [selectedCurrency];
        $[5] = selectedCurrency;
        $[6] = t5;
      } else {
        t5 = $[6];
      }
      t4 = t5;
      break bb0;
    }
    let t5;
    if ($[7] !== allCurrencies || $[8] !== shownCurrencies) {
      let t6;
      if ($[10] !== allCurrencies) {
        t6 = cur => allCurrencies.includes(cur);
        $[10] = allCurrencies;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      t5 = shownCurrencies.filter(t6);
      $[7] = allCurrencies;
      $[8] = shownCurrencies;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    const shown = t5;
    if (shown.length > 0) {
      t4 = shown;
      break bb0;
    }
    let t6;
    if ($[12] !== mainCurrency) {
      t6 = mainCurrency ? [mainCurrency] : [];
      $[12] = mainCurrency;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    t4 = t6;
  }
  const visibleCurrencies = t4;
  const isRemovable = isComparable && visibleCurrencies.length > 1;
  let t5;
  if ($[14] !== allCurrencies || $[15] !== chartData || $[16] !== visibleCurrencies) {
    t5 = {
      chartData,
      currencies: visibleCurrencies,
      allCurrencies
    };
    $[14] = allCurrencies;
    $[15] = chartData;
    $[16] = visibleCurrencies;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const data = useAffiliateChartData(t5);
  user?.dashboardLocale;
  let t6;
  if ($[18] !== chartData || $[19] !== intlLocale || $[20] !== user?.dashboardLocale || $[21] !== visibleCurrencies) {
    t6 = chartData ? getAffiliateChartOptions({
      currencies: visibleCurrencies,
      locale: user?.dashboardLocale,
      tooltipTitles: Object.keys(chartData.days).map(date => toFullDate(date, intlLocale))
    }) : undefined;
    $[18] = chartData;
    $[19] = intlLocale;
    $[20] = user?.dashboardLocale;
    $[21] = visibleCurrencies;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const options = t6;
  if (emptyStateProps) {
    let t7;
    if ($[23] !== t) {
      t7 = t("dashboard.affiliate.commission_earned");
      $[23] = t;
      $[24] = t7;
    } else {
      t7 = $[24];
    }
    let t8;
    if ($[25] !== t7) {
      t8 = <ChartCard.Title>{t7}</ChartCard.Title>;
      $[25] = t7;
      $[26] = t8;
    } else {
      t8 = $[26];
    }
    let t9;
    if ($[27] !== emptyStateProps) {
      t9 = <ChartCard.Body><EmptyState className="py-6" icon={EmptyStateChartIcon} {...emptyStateProps} /></ChartCard.Body>;
      $[27] = emptyStateProps;
      $[28] = t9;
    } else {
      t9 = $[28];
    }
    let t10;
    if ($[29] !== t8 || $[30] !== t9) {
      t10 = <ChartCard.Root>{t8}{t9}</ChartCard.Root>;
      $[29] = t8;
      $[30] = t9;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    return t10;
  }
  let t7;
  if ($[32] !== t) {
    t7 = t("dashboard.affiliate.commission_earned");
    $[32] = t;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== t7) {
    t8 = <ChartCard.Title>{t7}</ChartCard.Title>;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div aria-hidden="true" className="min-w-6 grow-1000" />;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== allCurrencies || $[38] !== chartData || $[39] !== isComparable || $[40] !== isRemovable || $[41] !== t || $[42] !== total || $[43] !== user?.dashboardLocale || $[44] !== visibleCurrencies) {
    t10 = total && chartData ? <><ul className="flex max-w-[calc(100%-1.875rem)] min-w-0 flex-wrap gap-1.5">{visibleCurrencies.map(cur_0 => <li key={cur_0}><Badge color="lightGray" size="lg" shape="rounded"><span aria-hidden="true" className="size-1.5 shrink-0 rounded-full" style={{
              backgroundColor: getAffiliateChartTextColor(cur_0, allCurrencies).color
            }} />{`${cur_0.toUpperCase()} • ${currencyFormat(centsToUnits(getChartValue(chartData.sums[cur_0] ?? cents(0))), cur_0, user?.dashboardLocale)}`}{isRemovable && <Badge.Close aria-label={t("dashboard.affiliate_dashboard.remove_currency", {
              currency: t(CurrencyNameEnum[cur_0])
            })} onClick={() => setShownCurrencies(visibleCurrencies.filter(shown_0 => shown_0 !== cur_0))} />}</Badge></li>)}</ul><div aria-hidden="true" className="min-w-1.5 grow" />{isComparable && <Select.Root multiple={true} aria-label={t("global.currency")} drawerTitle={t("global.currency")} items={allCurrencies.map(cur_1 => ({
        value: cur_1,
        label: t(CurrencyNameEnum[cur_1])
      }))} value={visibleCurrencies} onValueChange={next => {
        if (next?.length) {
          setShownCurrencies(next);
        }
      }}><Select.Trigger unstyled={true} render={<Button variant="tertiary-gray" size="icon-xs" />}><PlusIcon /></Select.Trigger><Select.Content><Select.List>{allCurrencies.map(cur_2 => <Select.Option key={cur_2} value={cur_2} disabled={visibleCurrencies.length === 1 && visibleCurrencies[0] === cur_2}>{t(CurrencyNameEnum[cur_2])}</Select.Option>)}</Select.List></Select.Content></Select.Root>}</> : <Skeleton className="h-6 w-40" />;
    $[37] = allCurrencies;
    $[38] = chartData;
    $[39] = isComparable;
    $[40] = isRemovable;
    $[41] = t;
    $[42] = total;
    $[43] = user?.dashboardLocale;
    $[44] = visibleCurrencies;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  let t11;
  if ($[46] !== t10 || $[47] !== t8) {
    t11 = <div className="flex flex-wrap items-start gap-y-1.5">{t8}{t9}{t10}</div>;
    $[46] = t10;
    $[47] = t8;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  let t12;
  if ($[49] !== chartData || $[50] !== data || $[51] !== height || $[52] !== options) {
    t12 = <ChartCard.Body>{chartData && options ? <Bar options={options} data={data} plugins={barChartPlugins} height={height} /> : <div style={{
        height
      }}><Skeleton /></div>}</ChartCard.Body>;
    $[49] = chartData;
    $[50] = data;
    $[51] = height;
    $[52] = options;
    $[53] = t12;
  } else {
    t12 = $[53];
  }
  let t13;
  if ($[54] !== t11 || $[55] !== t12) {
    t13 = <ChartCard.Root>{t11}{t12}</ChartCard.Root>;
    $[54] = t11;
    $[55] = t12;
    $[56] = t13;
  } else {
    t13 = $[56];
  }
  return t13;
}